import React, { useRef, useEffect } from 'react';
import './KIWerkstatt-v2.css';
import RegionalPartnersSection from '../components/RegionalPartnersSection';

const KIWerkstatt: React.FC = () => {
  const heroRef = useRef<HTMLElement | null>(null);

  // Ensure hero overlaps header on mobile exactly like page-header
  useEffect(() => {
    const applyMobileHeroOffset = () => {
      if (!heroRef.current) return;
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (!isMobile) {
        heroRef.current.style.marginTop = '';
        heroRef.current.style.paddingTop = '';
        return;
      }
      const header = document.querySelector<HTMLElement>('.header-container');
      const headerHeight = header ? header.offsetHeight : 70;
      heroRef.current.style.marginTop = `-${headerHeight}px`;
      // Keep existing vertical padding but add headerHeight to top
      // Default mobile top padding similar to page-header (2rem)
      const baseTopPaddingRem = 2;
      const baseTopPaddingPx = baseTopPaddingRem * 16; // assume 16px root
      const computedTopPadding = headerHeight + baseTopPaddingPx;
      heroRef.current.style.paddingTop = `${computedTopPadding}px`;
    };

    applyMobileHeroOffset();
    window.addEventListener('resize', applyMobileHeroOffset, { passive: true });
    window.addEventListener('orientationchange', applyMobileHeroOffset, { passive: true } as any);
    return () => {
      window.removeEventListener('resize', applyMobileHeroOffset as any);
      window.removeEventListener('orientationchange', applyMobileHeroOffset as any);
    };
  }, []);

  // Image Gallery Functionality
  useEffect(() => {
    // Setup for section 1 (original)
    const setupGallery = (sectionSelector: string) => {
      const section = document.querySelector(sectionSelector);
      if (!section) return null;

      let currentSlide = 0;
      const slides = section.querySelectorAll('.gallery-slide');
      const benefitItems = section.querySelectorAll('.benefit-item');

      if (slides.length === 0) return null;

      const showSlide = (index: number) => {
        slides.forEach(slide => slide.classList.remove('active'));
        benefitItems.forEach(item => item.classList.remove('active'));

        slides[index]?.classList.add('active');
        benefitItems[index]?.classList.add('active');
        currentSlide = index;
      };

      const nextSlide = () => {
        currentSlide = (currentSlide + 1) % slides.length;
        showSlide(currentSlide);
      };

      let autoplayInterval = setInterval(nextSlide, 4000);

      const benefitClickHandlers: (() => void)[] = [];
      const benefitHoverHandlers: (() => void)[] = [];
      benefitItems.forEach((item, index) => {
        const handler = () => {
          showSlide(index);
          clearInterval(autoplayInterval);
          autoplayInterval = setInterval(nextSlide, 4000);
        };
        benefitClickHandlers.push(handler);
        benefitHoverHandlers.push(handler);
        item.addEventListener('click', handler);
        item.addEventListener('mouseenter', handler);
      });

      benefitItems[0]?.classList.add('active');

      return () => {
        clearInterval(autoplayInterval);
        benefitItems.forEach((item, index) => {
          item.removeEventListener('click', benefitClickHandlers[index]);
          item.removeEventListener('mouseenter', benefitHoverHandlers[index]);
        });
      };
    };

    const cleanup1 = setupGallery('[data-section="ki-werkstatt-section-1"]');

    // Cleanup
    return () => {
      cleanup1?.();
    };
  }, []);

  return (
    <div className="page ki-werkstatt-page" data-timestamp="2024-09-23-14:35">
      {/* Hero Section - Same structure as course detail page */}
      <section
        ref={(el) => { heroRef.current = el; }}
        className="page-header ki-werkstatt-hero"
        data-section="ki-werkstatt-hero"
      >
        <div className="container">
          <div className="ki-werkstatt-hero-content">
            <span className="hero-pretitle">Willkommen in der</span>
            <h1 className="ki-werkstatt-hero-title">KI-Werkstatt</h1>
            <div className="hero-separator"></div>
            <p className="ki-werkstatt-hero-subtitle">
              Technik und Know-How für Deine KI-Projekte
            </p>
          </div>
        </div>
      </section>

      {/* Section 1: About */}
      <section className="ki-werkstatt-section" data-section="ki-werkstatt-section-1">
        <div className="container">
          <h2 className="section-title">Der <span className="highlight-text">KI-"Maker Space"</span> in Deiner Region</h2>
          <p className="section-subtitle">
            Modernste KI-Technik und KnowHow für deine KI-Projekte
          </p>
          <div className="ki-werkstatt-overlay-layout">
            <div className="overlay-gallery">
              <div className="gallery-container">
                <div className="gallery-slides">
                  <div className="gallery-slide active">
                    <picture>
                      <source srcSet="/assets/images/ki-werkstatt/kiwerkstatt_hardware.webp" type="image/webp" />
                      <img 
                        src="/assets/images/ki-werkstatt/kiwerkstatt_hardware.jpeg" 
                        alt="KI-Werkstatt Hardware"
                        width="1200"
                        height="900"
                        loading="eager"
                        decoding="async"
                        onError={(e) => {
                          e.currentTarget.src = '/assets/images/common/course-placeholder.svg';
                        }}
                      />
                    </picture>
                  </div>
                  <div className="gallery-slide">
                    <picture>
                      <source srcSet="/assets/images/ki-werkstatt/kiwerkstatt_software.webp" type="image/webp" />
                      <img 
                        src="/assets/images/ki-werkstatt/kiwerkstatt_software.jpeg" 
                        alt="KI-Werkstatt Software"
                        width="1200"
                        height="900"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          e.currentTarget.src = '/assets/images/common/course-placeholder.svg';
                        }}
                      />
                    </picture>
                  </div>
                  <div className="gallery-slide">
                    <picture>
                      <source srcSet="/assets/images/ki-werkstatt/kiwerkstatt_knowhow.webp" type="image/webp" />
                      <img 
                        src="/assets/images/ki-werkstatt/kiwerkstatt_knowhow.jpeg" 
                        alt="KI-Werkstatt Know-How"
                        width="1200"
                        height="900"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          e.currentTarget.src = '/assets/images/common/course-placeholder.svg';
                        }}
                      />
                    </picture>
                  </div>
                  <div className="gallery-slide">
                    <picture>
                      <source srcSet="/assets/images/ki-werkstatt/kiwerkstatt_experiment.webp" type="image/webp" />
                      <img 
                        src="/assets/images/ki-werkstatt/kiwerkstatt_experiment.jpeg" 
                        alt="KI-Werkstatt Experimentieren"
                        width="1200"
                        height="900"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          e.currentTarget.src = '/assets/images/common/course-placeholder.svg';
                        }}
                      />
                    </picture>
                  </div>
                </div>
              </div>
            </div>
            <div className="overlay-benefits">
              <div className="benefits-list">
                <div className="benefit-item benefit-overlay">
                  <div className="benefit-content">
                    <h4 className="benefit-heading">KI-Hardware</h4>
                    <p className="benefit-description">Verfügbare Plattformen: Nvidia Jetson Nano/NX/AGX/Thor, Nvidia DGX Spark/RTX & Apple Silicon. Ideal zum lokalen Training und Betrieb von genAI und Deep Learning Algorithmen.</p>
                  </div>
                </div>
                <div className="benefit-item benefit-overlay">
                  <div className="benefit-content">
                    <h4 className="benefit-heading">KI-Software</h4>
                    <p className="benefit-description">Fertig konfigurierte Software-Bausteile für lokale und Cloud-basierte RAG-Chatbots, Agenten und Deep Learning Projekte.</p>
                  </div>
                </div>
                <div className="benefit-item benefit-overlay">
                  <div className="benefit-content">
                    <h4 className="benefit-heading">KI-KnowHow</h4>
                    <p className="benefit-description">Wir entwickeln KI-Systeme seit 2012 in vielen Branchen. Profitiere von umfangreichem KnowHow aus mehreren hundert Projekten.</p>
                  </div>
                </div>
                <div className="benefit-item benefit-overlay">
                  <div className="benefit-content">
                    <h4 className="benefit-heading">Sicherer Raum zum Experimentieren</h4>
                    <p className="benefit-description">Unsere KI-Technik ist so aufgebaut, dass Dein KI-Projekt auch vollständig lokal ohne Internet- und Cloud-Verbindung umgesetzt werden kann. Deine sensiblen Betriebsdaten verlassen die KI-Werkstatt nicht.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Reference Project */}
      <section className="ki-werkstatt-section reference-project-section" data-section="ki-werkstatt-section-2">
        <div className="container">
          <h2 className="section-title">Die <span className="highlight-text">KI-Werkstatt</span> bei der Firma Wöhler</h2>
          <p className="section-subtitle">als innerbetrieblicher Innovationsort für KI-Entwicklungen</p>
          <div className="reference-project-content">
            <div className="video-container">
              <iframe
                src="https://www.youtube.com/embed/ZxLlVtJslnI"
                title="KI Werkstatt Referenzprojekt"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="youtube-video"
              />
            </div>
          </div>
        </div>
      </section>

      {/* NEW Section: Choose Your Workshop */}
      <section className="ki-werkstatt-section variant-selection-section" data-section="ki-werkstatt-variant-selection">
        <div className="container">
          <h2 className="section-title">
            Zwei inhaltliche <span className="highlight-text">Schwerpunkte</span>
          </h2>
          <p className="section-subtitle">
            für unterschiedliche betriebliche Anwendungsfelder
          </p>
          
          <div className="variant-cards">
            {/* Card 1: Deep Learning */}
            <div className="variant-card">
              <h3>Produktion</h3>
              <p className="variant-description">
                Sensor-basierte Deep Learning Anwendungen wie z.B. Computer Vision, Predictive Maintenance, Akustische Anomalieerkennung.
              </p>
              <ul className="variant-highlights">
                <li><strong>Anwendungsfelder:</strong> Anomalieerkennung, Arbeitssicherheit, Füllstandsüberwachung, Präsenzermittlung, uvm.</li>
                <li><strong>Methoden:</strong> Aufgabendefinition, Datenerhebung, Labelling, Training, Inferenzbetrieb</li>
                <li><strong>Integration:</strong> Implementierung im Maschinenpark, Schnittstellen zu Anlagensteuerungen, Wartung, uvm.</li>
                <li><strong>KI-Projektmanagement:</strong> Technische Architekturen, Strategien für Datenerhebung und Labelling, Wartbarkeit & Skalierbarkeit, uvm.</li>
              </ul>
              <a href="/ki-werkstatt/produktion" className="btn btn-primary-light">
                Infos und Anmeldung
              </a>
            </div>
            
            {/* Card 2: Local RAG */}
            <div className="variant-card">
              <h3>Verwaltung</h3>
              <p className="variant-description">
                Souveräne Nutzung von KI-Chatbots und Agenten auf lokalen Systemen. Verarbeitung von sensiblen Betriebsdaten über RAG.
              </p>
              <ul className="variant-highlights">
                <li><strong>Anwendungsfelder:</strong> Chatbots mit Betriebsgeheimnissen, automatische Email-Verarbeitung, Transkription und Zusammenfassung von Besprechungen, uvm.</li>
                <li><strong>Methoden:</strong> Lokale LLMs, RAG-Tools, Chunking, Embedding, Prompt-Parameter, Systemprompts, uvm.</li>
                <li><strong>Integration:</strong> Zugangskontrolle zu Betriebsdaten, Implementierung im Arbeitsalltag, Cyber-Security, uvm.</li>
                <li><strong>KI-Projektmanagement:</strong> Hardware-Plattformen, Solo vs. Team-Nutzung, uvm.</li>
              </ul>
              <a href="/ki-werkstatt/verwaltung" className="btn btn-primary-light">
                Infos und Anmeldung
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Regional Partners Map */}
      <RegionalPartnersSection />

      {/* CTA: inhouse event OR self-hosted KI-Werkstatt */}
      <section className="cta-section" data-section="ki-werkstatt-cta">
        <div className="container">
          <div className="cta-content">
            <h2>
              Die <span className="highlight-text">KI-Werkstatt</span> in Deinem Unternehmen.
            </h2>
            <p>
              Egal ob als Event oder dauerhafter Aufbau, wir bringen die KI-Werkstatt direkt in
              Deinen Betrieb. Als <strong>Firmen-Event</strong> oder mit dem Verkauf von
              spezieller <strong>Hardware und Software</strong> helfen wir Deinem Betrieb bei
              der Erschließung von KI-Potenzial.
            </p>
            <a href="/kontakt" className="btn btn-secondary-light">
              Jetzt Kontakt aufnehmen
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default KIWerkstatt;